import { createFileRoute } from "@tanstack/react-router";
import { FileText, ArrowLeftRight, MessageSquareMore, Plus, ScanSearch } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useWorkspaceNavigate } from "@/features/workspaces";

export const Route = createFileRoute("/workspaces/new")({
  component: NewWorkspaceTabPage,
});

export function NewWorkspaceTabPage() {
  const navigate = useWorkspaceNavigate();

  const handleSelectTool = (route: string, title: string) => {
    navigate({ route, title });
  };

  const handleCardKeyDown = (
    e: React.KeyboardEvent,
    route: string,
    title: string
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelectTool(route, title);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Select a Tool</h1>
          <p className="text-muted-foreground">
            Choose which tool to open in this workspace tab
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* SpecPilot Card */}
        <Card
          role="button"
          tabIndex={0}
          className="cursor-pointer hover:border-primary transition-colors"
          onClick={() => handleSelectTool("/specPilot", "SpecPilot")}
          onKeyDown={(e) => handleCardKeyDown(e, "/specPilot", "SpecPilot")}
        >
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FileText className="size-6 text-primary" />
              </div>
              <CardTitle>SpecPilot</CardTitle>
            </div>
            <CardDescription>
              Extract and manage submittals from PDF specifications
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              View existing jobs or create new submittal extractions
            </p>
          </CardContent>
        </Card>

        {/* RevPilot Card */}
        <Card
          role="button"
          tabIndex={0}
          className="cursor-pointer hover:border-primary transition-colors"
          onClick={() => handleSelectTool("/revPilot", "RevPilot")}
          onKeyDown={(e) => handleCardKeyDown(e, "/revPilot", "RevPilot")}
        >
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <ArrowLeftRight className="size-6 text-primary" />
              </div>
              <CardTitle>RevPilot</CardTitle>
            </div>
            <CardDescription>
              Compare revisions and track schedule changes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              View existing comparisons or create new revision analyses
            </p>
          </CardContent>
        </Card>

        {/* New SpecPilot Job Card */}
        <Card
          role="button"
          tabIndex={0}
          className="cursor-pointer hover:border-primary transition-colors"
          onClick={() => handleSelectTool("/specPilot/new", "New SpecPilot")}
          onKeyDown={(e) => handleCardKeyDown(e, "/specPilot/new", "New SpecPilot")}
        >
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Plus className="size-6 text-primary" />
              </div>
              <CardTitle>New SpecPilot Job</CardTitle>
            </div>
            <CardDescription>
              Start a new submittal extraction job
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Upload a PDF and extract submittal tables automatically
            </p>
          </CardContent>
        </Card>

        {/* New RevPilot Comparison Card */}
        <Card
          role="button"
          tabIndex={0}
          className="cursor-pointer hover:border-primary transition-colors"
          onClick={() => handleSelectTool("/revPilot/new", "New RevPilot")}
          onKeyDown={(e) => handleCardKeyDown(e, "/revPilot/new", "New RevPilot")}
        >
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Plus className="size-6 text-primary" />
              </div>
              <CardTitle>New RevPilot</CardTitle>
            </div>
            <CardDescription>
              Start a new revision comparison
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Compare two versions of a schedule to find changes
            </p>
          </CardContent>
        </Card>

        {/* TakeoffPilot Card */}
        <Card
          role="button"
          tabIndex={0}
          className="cursor-pointer hover:border-primary transition-colors"
          onClick={() => handleSelectTool("/takeoffPilot", "TakeoffPilot")}
          onKeyDown={(e) => handleCardKeyDown(e, "/takeoffPilot", "TakeoffPilot")}
        >
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <ScanSearch className="size-6 text-primary" />
              </div>
              <CardTitle>TakeoffPilot</CardTitle>
            </div>
            <CardDescription>
              Count schedule tags or build custom takeoffs across finish plans
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Start a tag-based takeoff workflow and export confirmed counts
            </p>
          </CardContent>
        </Card>

        {/* CollabPilot Card */}
        <Card
          role="button"
          tabIndex={0}
          className="cursor-pointer hover:border-primary transition-colors"
          onClick={() => handleSelectTool("/revCollab", "CollabPilot")}
          onKeyDown={(e) => handleCardKeyDown(e, "/revCollab", "CollabPilot")}
        >
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <MessageSquareMore className="size-6 text-primary" />
              </div>
              <CardTitle>CollabPilot</CardTitle>
            </div>
            <CardDescription>
              Collaborate on document revisions with annotations in real time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Open revision sessions and coordinate comments with your team
            </p>
          </CardContent>
        </Card>

        <Card
          role="button"
          tabIndex={0}
          className="cursor-pointer hover:border-primary transition-colors"
          onClick={() => handleSelectTool("/takeoffPilot/new", "New TakeoffPilot")}
          onKeyDown={(e) => handleCardKeyDown(e, "/takeoffPilot/new", "New TakeoffPilot")}
        >
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Plus className="size-6 text-primary" />
              </div>
              <CardTitle>New TakeoffPilot</CardTitle>
            </div>
            <CardDescription>
              Start a new takeoff review workflow
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Pick plan documents, optionally attach a schedule, then review the takeoff
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
